import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Eye, EyeOff, Gift, Loader2 } from "lucide-react";
import { Logo } from "@/components/Brand";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { apiError } from "@/lib/api";
import { toast } from "sonner";

export default function Login() {
  const { login, user } = useAuth();
  const { settings } = useSettings();
  const welcomePts = settings?.rewards?.enabled ? Number(settings?.rewards?.welcome_bonus_points ?? 20) : 0;
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const destFor = (u) => location.state?.from || (u.role === "super_admin" ? "/super" : u.role === "staff" ? "/staff" : (u.role === "admin" || u.role === "branch_admin") ? "/admin" : "/account");

  useEffect(() => {
    if (user && user.id) navigate(destFor(user), { replace: true });
  }, [user]); // eslint-disable-line

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const u = await login(email, password);
      toast.success("Welcome back!");
      navigate(destFor(u), { replace: true });
    } catch (e2) { setErr(apiError(e2)); } finally { setBusy(false); }
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/account";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-[calc(100vh-140px)] flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md bg-white border border-gray-100 rounded-3xl shadow-card p-8">
        <div className="flex justify-center mb-6"><Logo size="text-3xl" /></div>
        <h1 className="font-heading font-extrabold text-2xl text-center">Welcome back</h1>
        <p className="text-sm text-muted-foreground text-center mb-6">Sign in to reorder faster and earn rewards.</p>
        {welcomePts > 0 && (
          <div data-testid="login-welcome-bonus" className="flex items-center gap-3 bg-red-50 border border-red-100 rounded-2xl px-4 py-3 mb-5">
            <div className="bg-metros-red text-white rounded-full p-2 shrink-0"><Gift size={16} /></div>
            <p className="text-sm text-metros-black"><b>New here?</b> Create an account and get <b className="text-metros-red">{welcomePts} welcome points</b> instantly.</p>
          </div>
        )}
        {err && <div data-testid="login-error" className="bg-red-50 text-metros-red text-sm rounded-xl px-4 py-3 mb-4">{err}</div>}
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="text-sm font-semibold">Email</label>
            <input data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              className="w-full mt-1 rounded-xl border border-gray-200 px-4 py-3 focus:ring-2 focus:ring-metros-red focus:outline-none" />
          </div>
          <div>
            <label className="text-sm font-semibold">Password</label>
            <div className="relative mt-1">
              <input data-testid="login-password" type={show ? "text" : "password"} required value={password} onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-xl border border-gray-200 px-4 py-3 pr-11 focus:ring-2 focus:ring-metros-red focus:outline-none" />
              <button type="button" data-testid="toggle-password" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                {show ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            <div className="text-right -mt-1">
              <Link to="/forgot-password" data-testid="forgot-link" className="text-sm text-metros-red font-semibold">Forgot password?</Link>
            </div>
          </div>
          <button data-testid="login-submit" disabled={busy} className="w-full bg-metros-red hover:bg-metros-redhover text-white rounded-full py-3.5 font-bold flex justify-center items-center gap-2 active:scale-95 transition-all disabled:opacity-60">
            {busy && <Loader2 className="animate-spin" size={18} />} Sign In
          </button>
        </form>
        <div className="flex items-center gap-3 my-5">
          <div className="h-px bg-gray-200 flex-1" />
          <span className="text-xs text-muted-foreground font-semibold">OR</span>
          <div className="h-px bg-gray-200 flex-1" />
        </div>
        <button data-testid="google-login" type="button" onClick={loginWithGoogle}
          className="w-full border border-gray-200 hover:bg-gray-50 rounded-full py-3.5 font-bold flex justify-center items-center gap-3 active:scale-95 transition-all">
          <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="w-5 h-5" />
          Continue with Google
        </button>
        <p className="text-sm text-center mt-5 text-muted-foreground">
          New to METROS? <Link to="/register" data-testid="go-register" className="text-metros-red font-semibold">Create an account</Link>
        </p>
        <p className="text-sm text-center mt-2">
          <Link to="/menu" className="text-gray-500">Continue as guest →</Link>
        </p>
      </div>
    </div>
  );
}
